import { useEffect, useId, useSyncExternalStore } from "react";
import type { Middleware } from "swr";

// Ids of SWR hooks on their first fetch (no data yet). Background refreshes
// are ignored so polling never keeps the page looking busy.
const pending = new Set<string>();
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** SWR middleware: tracks which hooks are waiting on their first response. */
export const trackLoading: Middleware = (useSWRNext) => (key, fetcher, config) => {
  const swr = useSWRNext(key, fetcher, config);
  const id = useId();
  const busy = swr.isLoading;
  useEffect(() => {
    if (!busy) return;
    pending.add(id);
    emit();
    return () => {
      pending.delete(id);
      emit();
    };
  }, [busy, id]);
  return swr;
};

/** True while any tracked request is on its first load. */
export function useIsLoading() {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => pending.size > 0,
  );
}
